import { Card, Table, Typography, message } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import {
  DELETE_PRODUCTION_DETAILS,
  EXPORT_PRODUCTION_DETAILS,
  GET_PRODUCTION_DETAILS,
  SALES_CONTRACT_COST_REPORT,
  SUPPLIER_WISE_ITEM_PRICE_REPORT,
  VIEW_PRODUCTION_DETAILS,
} from "../../../apiServices/API_ENDPOINTS";
import { getData, postData } from "../../../apiServices/common";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import CustomFilter from "../../Common/CustomFilter";
import ExtraContent from "../../Configuration/CommonComponents/ExtraContent";
import FilterComponent from "../../Configuration/CommonComponents/FilterComponent";
import { cglFormatDate } from "utils/functions";
import { SalesContractCostReportPreviewContext } from "../../../common/SearchForm/FilterFields/SalesContractCostReportFields";

const SalesContractCostReport = () => {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Round 2 (B, R3): mounted-ref + request-sequence guards for previewFunction,
  // which SalesContractCostReportFields now also calls once on mount to load the
  // most-recently-updated commercial's preview.
  const mountedRef = useRef(true);
  const reqIdRef = useRef(0);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Antd
  const { Title } = Typography;

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const cutPath = (path) => {
    const updatedPath = path ? path.split("public/production_details/")[1] : "";
    return updatedPath;
  };

  const getAllDetails = useCallback(async (filterValues) => {
    setLoading(true);
    const query = `${GET_PRODUCTION_DETAILS}`;
    const bodyData = {
      per_page: filterValues?.per_page || 10,
      ...filterValues,
    };

    const response = await getData(query, false, bodyData);

    if (response?.status === 200) {
      setLoading(false);
      setTableData(response?.data?.data);
    } else {
      setLoading(false);
      message.error("Error getting details data");
    }
  }, []);

  const exportDetails = async (filterValues) => {
    const { sales_contract_id } = filterValues;
    const bodyData = {
      ...(sales_contract_id && { sales_contract_id }),
    };
    // if (Object.values(bodyData).length && !Object.values(bodyData).some((val) => val === undefined)) {
      setLoading(true);

      const query = `${SALES_CONTRACT_COST_REPORT}`;

      const response = await getData(query, false, bodyData, { "Content-Type": "blob" }, "arraybuffer");

      console.log(response)

      if (response && response?.data) {
        // Download the file
        const outputFilename = `sales_contract_cost_report.xlsx`;

        // If you want to download file automatically using link attribute.
        const url = URL.createObjectURL(
          new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", outputFilename);
        document.body.appendChild(link);
        link.click();
        link.remove();

        setLoading(false);
        message.success("Sales Contract cost report downloaded Successfully");
        refetch();
      } else {
        setLoading(false);
        message.error("Something went wrong");
      }
    // }
  };

  const previewFunction = async (filterValues) => {
    const myReq = ++reqIdRef.current;
    setLoading(true);

    const bodyData = {
      ...filterValues,
      report_type: "preview",
    };

    const query = `${SALES_CONTRACT_COST_REPORT}`;
    const response = await getData(query, false, bodyData);

    // Round 2 (B, R3): mounted guard + stale-response race guard (round-1 C1 pattern) —
    // this can now also be called from the mount-time default-selection in
    // SalesContractCostReportFields.
    if (!mountedRef.current || myReq !== reqIdRef.current) return;

    if (response?.data?.code === 200) {
      setLoading(false);
      setTableData(response?.data?.data);
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  // Effects
  // useEffect(() => {
  //   getAllDetails();
  // }, [refresh, getAllDetails]);

  const refetch = () => {
    setRefresh(refresh + 1);
  };

  const openFile = (name) => {
    const URL = `${process.env.REACT_APP_BASE_URL}/storage/production_details/${name}`;
    window.open(URL, "_blank", "noreferrer");
  };

  const tableColumns = [
    {
      title: "Style Name",
      dataIndex: "style_name",
      key: "style_name",
      width: 150,
      fixed: "left",
    },
    {
      title: "PI Number",
      dataIndex: "pi_number",
      key: "pi_number",
      width: 200,
      fixed: "left",
    },
    {
      title: "Goods Description",
      dataIndex: "goods_description",
      key: "goods_description",
      width: 150,
    },
    {
      title: "Supplier Name",
      dataIndex: "supplier_name",
      key: "supplier_name",
      width: 200,
    },
    {
      title: "Category",
      dataIndex: "category_name",
      key: "category_name",
      width: 150,
    },
    {
      title: "Item Name",
      dataIndex: "item_name",
      key: "item_name",
      width: 250,
    },
    {
      title: "Unit",
      dataIndex: "unit_name",
      key: "unit_name",
      width: 80,
      align: "center",
    },
    {
      title: "PI Qty",
      dataIndex: "pi_qty",
      key: "pi_qty",
      width: 120,
      align: "right",
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      width: 100,
      align: "right",
      // render: (value) => parseFloat(value).toFixed(4),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      align: "right",
      // render: (value) => parseFloat(value).toFixed(4),
    },
  ];

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={exportDetails}
        filterName={"sales_contract_costing_report"}
        searchButtonText={"Export"}
        defaultOpen
        setShowFilter={setShowFilter}
        showFilter={showFilter}
        isPreview={false}
        previewFunction={previewFunction}

      />
    ),
    [showFilter],
  );

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={exportDetails}
                hideAddButton={true}
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            }
            title={<Title level={5}>Sales Contract Cost Report</Title>}
          />
        }
        content={
          <>
            <div className="containt-body">
              {/* Round 2 (B, R3): SalesContractCostReportFields calls previewFunction once it
                  has picked the most-recently-updated commercial record on mount; it reaches
                  this closure via context since SearchForm/CustomFilter/FilterComponent (out
                  of this task's allow-list) don't thread previewFunction down to the filter
                  fields today. */}
              <SalesContractCostReportPreviewContext.Provider value={previewFunction}>
                <Card>{memoizedCustomFilter}</Card>
              </SalesContractCostReportPreviewContext.Provider>
              {/* <Table
                loading={loading}
                dataSource={tableData}
                columns={tableColumns.map((col, idx) => ({
                  ...col,
                  fixed: idx < 5 ? "left" : undefined,
                }))}
                pagination
                scroll={{
                  x: 1200,
                  y: 500,
                }}
                bordered
                sticky
              /> */}
            </div>
          </>
        }
      />
    </>
  );
};

export default SalesContractCostReport;
